import { useState, useEffect, useRef, useCallback } from 'react';
import { X, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { devLog, devError } from '../utils/logger';
import { canShowGigInterest } from '../utils/accessPolicy';
import { validateText } from '../utils/profanity-filter';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { FullscreenShell } from './layout/FullscreenShell';

interface ShowInterestModalProps {
  gigId: string;
  gigTitle: string;
  onClose: () => void;
  onSuccess: () => void;
  userSubscription: string;
  userType?: 'talent' | 'employer' | string;
  accessToken: string;
  /** When provided, used instead of location.hash for "go to subscription" */
  onNavigateToSubscription?: () => void;
}

export function ShowInterestModal({ 
  gigId, 
  gigTitle, 
  onClose, 
  onSuccess, 
  userSubscription,
  userType,
  accessToken,
  onNavigateToSubscription,
}: ShowInterestModalProps) {
  const CLOSE_ANIMATION_MS = 180;
  useModalFocus(true, { fullscreen: true }); // Full-screen modal: hide header/footer while open
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const isEmployerUser = userType === 'employer';

  // Centralized policy: who can show interest in gigs
  const hasAccess = canShowGigInterest({ subscription: userSubscription });

  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [isClosing, onClose]);

  const navigateToSubscription = useCallback(() => {
    // Close modal first to release global modal focus before navigation.
    requestClose();
    window.setTimeout(() => {
      if (onNavigateToSubscription) {
        onNavigateToSubscription();
      } else {
        window.location.hash = 'subscription';
      }
    }, CLOSE_ANIMATION_MS);
  }, [requestClose, onNavigateToSubscription]);

  // Browser history for back button
  useEffect(() => {
    window.history.pushState({ modal: 'show-interest' }, '');
    
    const handlePopState = (e: PopStateEvent) => {
      // If history moved TO this modal state (another nested layer closed), keep this modal open.
      if (e.state?.modal === 'show-interest') return;
      requestClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [requestClose]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!hasAccess) {
      toast.error(
        isEmployerUser
          ? 'Du må ha Arbeidsgiver Basis eller Arbeidsgiver Pluss for å vise interesse'
          : 'Du må ha Talentfunn Pluss for å vise interesse'
      );
      navigateToSubscription();
      return;
    }

    if (!message.trim()) {
      toast.error('Vennligst skriv en melding');
      return;
    }

    if (message.length > 200) {
      toast.error('Meldingen kan ikke være lengre enn 200 tegn');
      return;
    }
    const messageValidation = validateText(message, 'Melding');
    if (!messageValidation.isValid) {
      toast.error(messageValidation.error || 'Meldingen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/interest`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            message: message.trim()
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresUpgrade) {
          toast.error(data.error || 'Du må oppgradere for å vise interesse');
          navigateToSubscription();
          return;
        }
        throw new Error(data.error || 'Kunne ikke sende interesse');
      }

      devLog('✅ Interest sent successfully:', data);
      toast.success('Interesse sendt! Arbeidsgiver vil se din profil og melding.');
      onSuccess();
      requestClose();

    } catch (error) {
      devError('Error sending interest:', error);
      toast.error(error instanceof Error ? error.message : 'Kunne ikke sende interesse');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = message.length;
  const isValid = message.trim().length > 0 && characterCount <= 200;

  return (
    <FullscreenShell
      title="Vis interesse"
      onClose={requestClose}
      onBack={requestClose}
      containerClassName="xl:max-w-lg"
      overlayMotionProps={{
        initial: { opacity: 0 },
        animate: { opacity: isClosing ? 0 : 1 },
        transition: { duration: 0.18, ease: 'easeOut' },
      }}
      contentClassName="space-y-4"
    >
      {/* Gig title */}
      <div className="bg-purple-50 p-4 rounded-lg">
        <p className="text-sm text-purple-700 font-medium mb-1">Oppdrag:</p>
        <p className="text-purple-900 font-semibold">{gigTitle}</p>
      </div>

      {!hasAccess ? (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-200">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-purple-900 mb-2">Abonnement kreves</h3>
              <p className="text-purple-800 text-sm mb-3">
                {isEmployerUser
                  ? 'For å vise interesse for oppdrag må du ha Arbeidsgiver Basis eller Arbeidsgiver Pluss. Du får:'
                  : 'For å vise interesse for oppdrag må du ha Talentfunn Pluss. Med Talentfunn Pluss får du:'}
              </p>
              <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
                {isEmployerUser ? (
                  <>
                    <li>Vis interesse for oppdrag</li>
                    <li>Arbeidsgiver Basis: ubegrensede meldinger til talenter</li>
                    <li>Arbeidsgiver Pluss: publiser oppdrag, favoritter og lagrede søk</li>
                  </>
                ) : (
                  <>
                    <li>Vis interesse for oppdrag</li>
                    <li>6 portfolioprosjekter</li>
                    <li>Videoer i portfolioprosjekter</li>
                    <li>3x høyere synlighet i søkeresultater</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          <button
            onClick={navigateToSubscription}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
          >
            Se abonnement
          </button>
        </div>
      ) : (
        <>
          <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Arbeidsgiver vil se din profil og denne meldingen. Skriv en kort presentasjon eller hvorfor du er interessert.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-purple-900 mb-2">Din melding</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="F.eks: Jeg er interessert i dette oppdraget. Jeg har erfaring med..."
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              maxLength={200}
              disabled={isSubmitting}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">Maks 200 tegn</p>
              <p
                className={`text-sm font-medium ${
                  characterCount > 200 ? 'text-red-600' : characterCount > 180 ? 'text-orange-600' : 'text-gray-600'
                }`}
              >
                {characterCount}/200
              </p>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className={`w-full px-6 py-3 rounded-lg font-semibold transition-all ${
              !isValid || isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
            }`}
          >
            {isSubmitting ? 'Sender interesse...' : 'Send interesse'}
          </button>
        </>
      )}
    </FullscreenShell>
  );
}
